import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  className = "",
  disabled = false,
  searchPlaceholder = "Search...",
  displayKey = 'label',
  valueKey = 'value',
  maxHeight = '300px',
  onOpen  // Optional callback when dropdown opens
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const displayValue = typeof option === 'object' ? option[displayKey] : option;
    return displayValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Toggle selection of an option
  const toggleOption = (option) => {
    const optionValue = typeof option === 'object' ? option[valueKey] : option;
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  // Remove a selected value
  const removeValue = (e, valueToRemove) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== valueToRemove));
  };

  // Get display text for a value
  const getDisplayText = (val) => {
    const option = options.find(opt => 
      typeof opt === 'object' ? opt[valueKey] === val : opt === val
    );
    return option ? (typeof option === 'object' ? option[displayKey] : option) : val;
  };

  // Clear all selections
  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main dropdown trigger */}
      <div
        className={`flex items-center justify-between w-full pl-4 pr-2 py-2 border border-gray-300 rounded-full shadow-sm bg-white cursor-pointer
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-black focus:border-black'}
        `}
        onClick={() => {
          if (!disabled) {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);
            // Call onOpen callback when opening (not closing)
            if (newIsOpen && onOpen) {
              onOpen();
            }
          }
        }}
      >
        <div className="flex-1 flex gap-1 items-center min-h-[24px] overflow-hidden">
          {value.length === 0 ? (
            <span className="text-gray-500">{placeholder}</span>
          ) : (
            <>
              {value.slice(0, 1).map((val) => (
                <span
                  key={val}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full flex-shrink-0"
                >
                  {getDisplayText(val)}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-blue-900"
                    onClick={(e) => removeValue(e, val)}
                  />
                </span>
              ))}
              {value.length > 1 && (
                <span className="text-xs text-gray-600 flex-shrink-0">
                  +{value.length - 1} more
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 ml-2">
          {value.length > 0 && (
            <X
              className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={clearAll}
            />
          )}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Select all / Clear all */}
          <div className="px-2 py-1 border-b border-gray-200">
            <div className="flex justify-between items-center text-xs">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const allValues = filteredOptions.map(opt => 
                    typeof opt === 'object' ? opt[valueKey] : opt
                  );
                  onChange(allValues);
                }}
                className="text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
              >
                Select All
              </button>
              <span className="text-gray-500">
                {value.length} selected
              </span>
              <button
                onClick={clearAll}
                className="text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Options list */}
          <div 
            className="overflow-y-auto"
            style={{ maxHeight }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const optionValue = typeof option === 'object' ? option[valueKey] : option;
                const optionDisplay = typeof option === 'object' ? option[displayKey] : option;
                const isSelected = value.includes(optionValue);

                return (
                  <div
                    key={optionValue}
                    className={`flex items-center justify-between px-4 py-2 cursor-pointer text-sm
                      ${isSelected ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'}
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(option);
                    }}
                  >
                    <span className="flex-1">{optionDisplay}</span>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;